/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { SeaDacLiteDriver } from '../lib/seadac-lite-driver';
import { seaDacConnectedEvent } from '../../+state/usb.actions';
import { selectAuxRelayState } from '../../../call/+state/cchub.selectors';
import { AUX_RELAYS } from '../../../core/model/aux-relay';
import { EnvironmentService } from '../../../core/services/environment.service';

/**
 * Drives the Sealevel SeaDAC Lite 8115 USB relay module from ACD agent status.
 */
@Injectable({ providedIn: 'root' })
export class SeaDacService {
    private readonly driver = new SeaDacLiteDriver();
    private relayMonitor$ = new Subject<void>();
    private started = false;

    private readonly connectListener = (event: USBConnectionEvent) => {
        if (SeaDacService.matches(event.device)) {
            console.debug(`${SeaDacLiteDriver.DEVICE_NAME} connected`);
            this.connect(event.device).catch((e) => console.error(`${SeaDacLiteDriver.DEVICE_NAME}: failed to open after plug-in`, e));
        }
    };

    private readonly disconnectListener = (event: USBConnectionEvent) => {
        if (SeaDacService.matches(event.device)) {
            console.warn(`${SeaDacLiteDriver.DEVICE_NAME} disconnected`);
            this.handleDisconnect().catch((e) => console.error(`${SeaDacLiteDriver.DEVICE_NAME}: failed to release the module after unplug`, e));
        }
    };

    private readonly unloadListener = () => {
        this.driver.deEnergizeAllNow().catch(() => undefined);
    };

    constructor(
        private store: Store,
        private env: EnvironmentService
    ) {}

    public get enabled(): boolean {
        return Boolean(this.env.environment.featureFlags?.enableSeaDacRelay);
    }

    public init(): void {
        if (this.started || !this.enabled) {
            return;
        }
        if (!navigator.usb) {
            console.warn(`${SeaDacLiteDriver.DEVICE_NAME}: WebUSB is unavailable, relay support disabled`);
            return;
        }
        this.started = true;

        navigator.usb.addEventListener('connect', this.connectListener);
        navigator.usb.addEventListener('disconnect', this.disconnectListener);
        globalThis.addEventListener('beforeunload', this.unloadListener);

        navigator.usb
            .getDevices()
            .then((devices) => devices.find((device) => SeaDacService.matches(device)))
            .then(async (device) => {
                if (device) {
                    await this.connect(device);
                    return;
                }
                // WebUSB only reveals devices this origin has already been granted, so an unpaired module is
                // indistinguishable from an absent one. Rather than raise a chooser the call taker did not ask
                // for, pairing is left to an explicit action; once granted, getDevices() finds it on every load.
                console.debug(`${SeaDacLiteDriver.DEVICE_NAME}: not paired with this origin, awaiting explicit pairing`);
            })
            .catch((e) => console.error(`${SeaDacLiteDriver.DEVICE_NAME}: failed to enumerate paired devices`, e));
    }

    public async requestDevice(): Promise<void> {
        if (!this.enabled) {
            return;
        }
        try {
            const device = await navigator.usb.requestDevice({
                filters: [{ vendorId: SeaDacLiteDriver.VENDOR_ID, productId: SeaDacLiteDriver.PRODUCT_ID }]
            });
            await this.connect(device);
        } catch (e) {
            console.error(`${SeaDacLiteDriver.DEVICE_NAME}: device selection failed`, e);
        }
    }

    private async connect(device: USBDevice): Promise<void> {
        if (this.driver.opened) {
            return;
        }
        try {
            await this.driver.open(device);
            this.monitorRelayState();
            this.store.dispatch(seaDacConnectedEvent({ connected: true }));
            console.debug(`${SeaDacLiteDriver.DEVICE_NAME}: open, driving relays`);
        } catch (e) {
            console.error(`${SeaDacLiteDriver.DEVICE_NAME}: failed to open`, e);
            // open() may have claimed the interface before failing, so release it rather than leaking the claim.
            await this.driver.close();
            this.store.dispatch(seaDacConnectedEvent({ connected: false }));
        }
    }

    /**
     * Binds each relay to the agent status it is mapped to. distinctUntilChanged keeps the relays from
     * chattering, since the derived boolean re-emits on every unrelated agent status change.
     *
     * The mapping is the same one the CC-Hub drives from its on-board relays, so a position behaves
     * identically whichever hardware is fitted, and consistently if it happens to have both.
     */
    private monitorRelayState(): void {
        this.relayMonitor$.next();
        AUX_RELAYS.forEach((relay) =>
            this.store
                .select(selectAuxRelayState(relay))
                .pipe(distinctUntilChanged(), takeUntil(this.relayMonitor$))
                .subscribe((energized) =>
                    this.driver
                        .setRelay(relay, energized)
                        .then(() => console.debug(`${energized ? 'Energized' : 'De-energized'} ${relay}`))
                        .catch((e) => console.error(`Failed to ${energized ? 'energize' : 'de-energize'} ${relay}`, e))
                )
        );
    }

    private async handleDisconnect(): Promise<void> {
        this.relayMonitor$.next();
        await this.driver.close();
        if (!this.driver.opened) {
            this.store.dispatch(seaDacConnectedEvent({ connected: false }));
        }
    }

    public async destroy(): Promise<void> {
        if (!this.started) {
            return;
        }
        navigator.usb.removeEventListener('connect', this.connectListener);
        navigator.usb.removeEventListener('disconnect', this.disconnectListener);
        globalThis.removeEventListener('beforeunload', this.unloadListener);
        this.started = false;
        await this.handleDisconnect();
    }

    private static matches(device: USBDevice): boolean {
        return device.vendorId === SeaDacLiteDriver.VENDOR_ID && device.productId === SeaDacLiteDriver.PRODUCT_ID;
    }
}
